import type { GameStateView, Meld, RuleSet, TileId } from '@mj/protocol';
import { baseOfAka, isFlower } from '@mj/protocol';
import { shanten, nukiTileSet } from '@mj/engine';

/** hand + drawn (when non-null) — mirrors TableGame.humanDecisionHand. */
export function decisionHand(hand: TileId[], drawn: TileId | null): TileId[] {
  return drawn !== null ? [...hand, drawn] : [...hand];
}

/** ankan bases (4 copies in the decision hand) — mirrors TableGame.humanAnkanTiles. */
export function ankanTiles(decisionHand: TileId[]): TileId[] {
  const counts = new Map<TileId, number>();
  for (const t of decisionHand) {
    const b = baseOfAka(t);
    counts.set(b, (counts.get(b) ?? 0) + 1);
  }
  const out: TileId[] = [];
  for (const [b, c] of counts) if (c >= 4) out.push(b);
  return out;
}

/** shouminkan bases: decision-hand tiles whose base extends an existing pon meld — humanShouminkanTiles. */
export function shouminkanTiles(decisionHand: TileId[], melds: Meld[]): TileId[] {
  const ponBases = new Set(melds.filter((m) => m.kind === 'pon').map((m) => baseOfAka(m.tiles[0]!)));
  const out = new Set<TileId>();
  for (const t of decisionHand) {
    const b = baseOfAka(t);
    if (ponBases.has(b)) out.add(b);
  }
  return [...out];
}

/** allowed nuki honors held in the decision hand (empty unless sanmaKitaMode==='nukiDora') — humanNukiTiles. */
export function nukiTiles(decisionHand: TileId[], rs: RuleSet): TileId[] {
  if (rs.flags.sanmaKitaMode !== 'nukiDora') return [];
  const allowed: string[] = nukiTileSet(rs) as string[];
  const held = new Set<string>(decisionHand.map((t) => baseOfAka(t) as string));
  return allowed.filter((t) => held.has(t)) as TileId[];
}

/** flowers held in the decision hand (empty unless rs.tiles.flowers===true) — humanFlowerTiles. */
export function flowerTiles(decisionHand: TileId[], rs: RuleSet): TileId[] {
  if (rs.tiles.flowers !== true) return [];
  return decisionHand.filter(isFlower);
}

/** all four winds held AND wind-set yaku enabled — humanCanWindKan. */
export function canWindKan(decisionHand: TileId[], rs: RuleSet): boolean {
  if (rs.enabledYaku['wind-set']?.enabled !== true) return false;
  const held = new Set<string>(decisionHand.map((t) => baseOfAka(t) as string));
  return ['z1', 'z2', 'z3', 'z4'].every((w) => held.has(w));
}

/** tsumo-able now. phase 'draw' tests hand+drawn; 'discard' (post-kan) tests hand alone — humanCanTsumo. */
export function canTsumo(
  hand: TileId[],
  melds: Meld[],
  phase: GameStateView['phase'],
  drawn: TileId | null,
): boolean {
  if (phase === 'draw') return drawn !== null && shanten([...hand, drawn], melds) === -1;
  return shanten(hand, melds) === -1;
}
